import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeftIcon, PhoneIcon, PackageIcon, CheckCircleIcon, CoinsIcon, TrashIcon, UserIcon } from "@phosphor-icons/react";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function DeliveryBoyProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [boy, setBoy] = useState(null);
  const [activities, setActivities] = useState([]);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(todayStr());

  const load = useCallback(async () => {
    try {
      const roster = await api.get("/delivery-boys/roster");
      const found = (roster.data || []).find((b) => b.id === id);
      if (!found) { toast.error("Delivery boy not found"); nav("/delivery-boys"); return; }
      setBoy(found);
      const acts = await api.get(`/boy-activities?delivery_boy=${encodeURIComponent(found.name)}&from_date=${from}&to_date=${to}`);
      setActivities(acts.data || []);
    } catch { toast.error("Failed to load profile"); }
  }, [id, from, to, nav]);

  useEffect(() => { load(); }, [load]);

  if (!boy) return <div className="text-slate-400 overline p-6" data-testid="boy-loading">Loading…</div>;

  const totals = activities.reduce((acc, a) => {
    acc.deliveries += a.deliveries || 0;
    acc.invoices += a.invoices_confirmed || 0;
    acc.payment += (a.payment_cleared || 0) + (a.payment_pending || 0);
    return acc;
  }, { deliveries: 0, invoices: 0, payment: 0 });

  return (
    <div className="space-y-8" data-testid="boy-profile-page">
      <div>
        <Link to="/delivery-boys" className="overline text-brand-orange-ink inline-flex items-center gap-1 hover:text-brand-orange" data-testid="boy-profile-back">
          <ArrowLeftIcon size={12} /> Back to roster
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-brand-orange-ink">Profile</div>
          <h1 className="font-display text-4xl font-black text-brand-blue flex items-center gap-3" data-testid="boy-profile-name">
            <span className="w-10 h-10 bg-brand-blue text-brand-orange flex items-center justify-center">
              <UserIcon size={22} weight="fill" />
            </span>
            {boy.name}
          </h1>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <span className={`inline-flex px-2 py-0.5 border text-xs uppercase tracking-wider ${boy.active ? "border-brand-blue text-brand-blue" : "border-slate-300 text-slate-400"}`} data-testid="boy-profile-status">
              {boy.active ? "Active" : "Inactive"}
            </span>
            {boy.phone && (
              <span className="inline-flex items-center gap-1 text-sm text-slate-600 font-mono-num" data-testid="boy-profile-phone">
                <PhoneIcon size={12} /> {boy.phone}
              </span>
            )}
            {boy.note && <span className="text-sm text-slate-500">· {boy.note}</span>}
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="boy-profile-from" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="boy-profile-to" />
          </div>
        </div>
      </div>

      {/* Activity KPIs — Deliveries, Invoices Confirmed, Payment (per user spec) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-slate-200 bg-white">
        <ProfileKpi label="Deliveries" value={totals.deliveries} icon={PackageIcon} testid="boy-kpi-deliveries" />
        <ProfileKpi label="Invoices Confirmed" value={totals.invoices} icon={CheckCircleIcon} testid="boy-kpi-invoices" />
        <ProfileKpi label="Payment" value={money(totals.payment)} icon={CoinsIcon} accent testid="boy-kpi-payment" />
      </div>

      {/* Activity table */}
      <section>
        <div className="overline text-brand-orange-ink mb-3">Activity log · {activities.length} entries</div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-brand-blue-tint border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Date</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Deliveries</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Invoices Confirmed</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Payment</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Note</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((a) => {
                const payment = (a.payment_cleared || 0) + (a.payment_pending || 0);
                return (
                  <TableRow key={a.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`profile-activity-${a.id}`}>
                    <TableCell className="font-mono-num text-xs text-slate-600">{a.date}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{a.deliveries || 0}</TableCell>
                    <TableCell className="text-right font-mono-num">{a.invoices_confirmed || 0}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold text-brand-blue">{money(payment)}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{a.note || "—"}</TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={async () => {
                          if (!confirm(`Delete activity on ${a.date}?`)) return;
                          try { await api.delete(`/boy-activities/${a.id}`); toast.success("Deleted"); load(); }
                          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        }}
                        className="text-slate-400 hover:text-[#FF2400]"
                        data-testid={`profile-activity-delete-${a.id}`}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {activities.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No activity in this range.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

function ProfileKpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`p-6 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-brand-blue text-white" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`overline ${accent ? "text-white/70" : "text-brand-orange-ink"}`}>{label}</span>
        <Icon size={16} className={accent ? "text-white/70" : "text-brand-blue"} />
      </div>
      <div className="font-mono-num text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}
